import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Send, Copy, CheckCircle, BookOpen, MessageCircle,
  Target, ArrowRight, FileText
} from '../../components/icons';

const resources = [
  {
    id: 'prospect_invitation',
    icon: Send,
    title: 'Prospect Invitation Message',
    content: `Hi [First Name],

I would like to invite you to complete the Human Potential Profile. It is a meaningful assessment that provides insight into how you currently approach your goals, challenges and personal development.

It takes about 15 minutes to complete, and I will personally review the results with you in a complimentary conversation.

Here is your personal link:
[Assessment Link]

Looking forward to sharing the insights with you.

Best,
[Your Name]`,
  },
  {
    id: 'client_invitation',
    icon: MessageCircle,
    title: 'Client Invitation Message',
    content: `Hi [First Name],

As part of our coaching work together, I would like to invite you to complete the Human Potential Profile. It will help me understand your current patterns more deeply and identify the most meaningful areas for us to focus on.

It takes about 15 minutes to complete. Once you are done, I will review the results and we will discuss the insights in our next session.

Here is your personal link:
[Assessment Link]

[Your Name]`,
  },
  {
    id: 'review_structure',
    icon: BookOpen,
    title: 'Profile Review Conversation Structure',
    content: `1. Opening (5 min)
- Thank them for completing the Profile
- Explain that the results are a starting point, not a label
- Set the intention: to explore, not to judge

2. Overview (10 min)
- Share the high-level summary
- Ask: "What stands out to you?"
- Listen for what resonates and what surprises them

3. Deep Dive (20 min)
- Focus on 1-2 areas they want to explore
- Use the AI analysis as conversation starters
- Ask: "How does this relate to what you are experiencing right now?"

4. Opportunity Identification (15 min)
- Ask: "What is the biggest opportunity you see from this?"
- Discuss what would change if they addressed it
- Connect to their current goals

5. Next Steps (10 min)
- Summarise the key insight
- Suggest a natural next step (coaching, a specific action, etc.)
- Ask: "Would it be helpful if we continued this conversation?"`,
  },
  {
    id: 'opening_questions',
    icon: MessageCircle,
    title: 'Suggested Opening Questions',
    content: `1. "What was your first reaction when you saw the results?"
2. "Which part of the Profile felt most accurate to you?"
3. "Was there anything that surprised you?"
4. "Which area would you most like to explore today?"
5. "How does what you see here relate to what brought you to coaching?"
6. "What pattern, if shifted, would make the biggest difference for you?"
7. "What does the Profile tell you about how you approach challenges?"
8. "Where do you see the greatest opportunity for growth?"`,
  },
  {
    id: 'discuss_without_labelling',
    icon: BookOpen,
    title: 'How to Discuss Insights Without Labelling the Person',
    content: `Key principles:
- Use "the Profile suggests..." rather than "you are..."
- Frame patterns as tendencies, not fixed traits
- Ask the respondent to validate or challenge each insight
- Focus on how a pattern shows up, not on the pattern itself
- Use the respondent's own language to describe what they see
- Avoid diagnostic or clinical language
- Keep the tone curious and exploratory, not definitive

Example reframes:
- Instead of "You avoid conflict" → "The Profile suggests a pattern of prioritising harmony over directness. Does that resonate?"
- Instead of "You are risk-averse" → "The results show a preference for thoroughness before action. How does that show up in your decisions?"`,
  },
  {
    id: 'identify_opportunities',
    icon: Target,
    title: 'How to Identify Coaching Opportunities',
    content: `Look for:
1. Gaps between how the person sees themselves and how the Profile presents them
2. Patterns that appear across multiple areas of the Profile
3. Areas where the person has high awareness but low action
4. Recurring themes that connect to what brought them to coaching
5. Strengths that are being underused
6. Patterns that may be serving them in one context but limiting them in another

Ask:
- "Which of these patterns, if addressed, would have the biggest impact?"
- "Where do you feel most stuck right now?"
- "What would change if this pattern shifted?"`,
  },
  {
    id: 'transition_to_paid',
    icon: ArrowRight,
    title: 'How to Transition Naturally Into a Paid Offer',
    content: `1. Anchor on the insight
"We have identified something that could make a real difference for you."

2. Describe the path
"Based on what we have uncovered, I think the most effective way to work on this would be through a structured coaching engagement."

3. Offer the next step
"I work with clients over [duration] to address exactly this kind of pattern. Would it be useful if I shared what that looks like?"

4. Make it natural
- Do not rush the transition
- Let the respondent express interest first
- Use their language and their stated goals
- Frame the offer as the logical next step from the insight

5. If they are not ready
- That is perfectly fine
- Offer a follow-up conversation
- Leave the door open without pressure`,
  },
  {
    id: 'follow_up_email',
    icon: FileText,
    title: 'Follow-Up Email Template',
    content: `Subject: Your Profile Insights & Next Steps

Hi [First Name],

Thank you for taking the time to complete the Human Potential Profile and for our conversation today.

As we discussed, the key insight that emerged was:
[Insert the 1-2 key patterns or opportunities you identified]

If you would like to explore this further, I would be happy to continue the conversation. Here is what working together could look like:
[Brief description of your coaching offer]

If now is not the right time, that is completely fine. The Profile results will remain available, and you can reach out whenever you are ready.

Warm regards,
[Your Name]`,
  },
  {
    id: 'profile_to_client_checklist',
    icon: CheckCircle,
    title: 'Profile-to-Client Checklist',
    content: `Before the conversation:
- [ ] Review the AI analysis
- [ ] Identify 2-3 conversation starters
- [ ] Prepare the opening question
- [ ] Set a calm, curious intention

During the conversation:
- [ ] Open with their reaction, not your interpretation
- [ ] Let them validate or challenge each insight
- [ ] Focus on 1-2 areas, not everything
- [ ] Identify the biggest opportunity
- [ ] Use their language, not labels

After the conversation:
- [ ] Send the follow-up email within 24 hours
- [ ] Reference the specific insight you discussed
- [ ] Offer the next step without pressure
- [ ] Log the outcome in your dashboard`,
  },
];

export function ProfileResources() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyContent = (id: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="min-h-screen freud-bg py-8 px-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="freud-heading-2xl text-chocolate-900">Profile Resources</h1>
          <p className="freud-text-base text-chocolate-500">
            Templates, structures and guides for conducting powerful Profile conversations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {resources.map((resource, index) => (
            <motion.div
              key={resource.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="freud-card-elevated p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-sage-100 rounded-xl">
                  <resource.icon className="w-5 h-5 text-sage-600" />
                </div>
                <h3 className="freud-text-base font-semibold text-chocolate-900">{resource.title}</h3>
              </div>
              <div className="bg-cream-50 rounded-xl p-4 mb-3 max-h-64 overflow-y-auto">
                <pre className="freud-text-sm text-chocolate-600 whitespace-pre-wrap font-sans leading-relaxed">
                  {resource.content}
                </pre>
              </div>
              <button
                onClick={() => copyContent(resource.id, resource.content)}
                className="flex items-center gap-2 text-sage-600 hover:text-sage-700 font-medium freud-text-sm transition-colors"
              >
                {copiedId === resource.id ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy to clipboard
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
